/* File: src/contexts/game/gameEnd.js */
import { auth } from '../../firebaseConfig'; // Firebase auth instance
import { generateFeedbackMessage } from '../gameFeedback'; // Helper for generating user feedback

// Define API endpoint URLs
const deployedBaseUrl = "https://us-central1-dual-n-back-web-app.cloudfunctions.net";
const emulatorBaseUrl = "http://127.0.0.1:5001/dual-n-back-web-app/us-central1";

// Auto-detect local development environment for emulator usage
const isLocalDevelopment = typeof window !== 'undefined' &&
                           (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
const functionBaseUrl = isLocalDevelopment ? emulatorBaseUrl : deployedBaseUrl;
const calculateResultsUrl = `${functionBaseUrl}/calculateAndSaveResultsHttp`; // URL for the results calculation function

// Enhanced Helper function to sanitize data for JSON stringification with cycle detection
// Prevents errors when trying to stringify complex objects (like React state or refs)
function sanitizeForJSON(obj, depth = 0, maxDepth = 15, visited = new Set()) {
    if (depth > maxDepth) return "[Max Depth Exceeded]"; // Prevent infinite recursion
    if (typeof window !== 'undefined' && obj === window) return "[Window Object]";
    if (typeof document !== 'undefined' && obj === document) return "[Document Object]";
    if (typeof obj?.nodeType === 'number' && typeof obj?.nodeName === 'string') return `[DOM Element <${obj.nodeName}>]`; // Basic DOM element check
    if (obj instanceof Event) return `[Event type: ${obj.type}]`; // Handle Event objects
    if (obj?.$$typeof === Symbol.for('react.element')) return "[React Element]"; // Handle React elements
    if (obj === null || typeof obj !== 'object') return obj; // Handle primitives and null
    if (visited.has(obj)) return "[Circular Reference Detected]"; // Detect circular references
    visited.add(obj); // Mark object as visited for this path
    let sanitizedData = null;
    try {
        if (obj instanceof Date) sanitizedData = obj.toISOString(); // Serialize Dates
        else if (Array.isArray(obj)) { // Recursively sanitize array elements
            sanitizedData = [];
            for (const item of obj) { if (typeof item !== 'function' && typeof item !== 'undefined' && typeof item !== 'symbol') { sanitizedData.push(sanitizeForJSON(item, depth + 1, maxDepth, visited)); } }
        } else { // Recursively sanitize object properties
            sanitizedData = {};
            for (const key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) { const value = obj[key]; if (typeof value !== 'function' && typeof value !== 'undefined' && typeof value !== 'symbol') { sanitizedData[key] = sanitizeForJSON(value, depth + 1, maxDepth, visited); } } }
        }
    } catch (error) { console.error(`[sanitizeForJSON] Error during recursion at depth ${depth}:`, error); sanitizedData = "[Sanitization Error]"; }
    finally { visited.delete(obj); } // Remove from visited set after processing its branch
    return sanitizedData;
}


/**
 * Factory function returning the *logic* for ending a game session.
 * This function encapsulates the process of calculating results (potentially skipping),
 * updating profile state, and transitioning the game state appropriately.
 * It accepts an options object to modify its behavior, such as skipping the results
 * calculation and display, which is useful when abandoning a session via navigation.
 *
 * @param {object} dependencies - Dependencies required for the action.
 * @param {object|null} dependencies.activeProfile - The current user profile object.
 * @param {boolean} dependencies.isPlaygroundSession - Flag indicating if it's a playground session.
 * @param {boolean} dependencies.saveToProfile - Flag indicating if results should update profile progress.
 * @param {object} dependencies.challengeState - Current state of challenge mode.
 * @param {Function} dependencies.setChallengeState - Setter for challenge state.
 * @param {Function} dependencies.logReplayEvent - Function to log replay events.
 * @param {Function} dependencies.setGameState - Setter for the main game state.
 * @param {Function} dependencies.setGameResults - Setter for the game results object.
 * @param {Function} dependencies.setFeedbackMessage - Setter for user feedback messages.
 * @param {Function} dependencies.setIsPlaygroundSession - Setter for playground mode flag.
 * @param {Function} dependencies.setPlaygroundSettings - Setter for playground settings.
 * @param {Function} dependencies.setCurrentTrial - Setter for the current trial index.
 * @param {Function} dependencies.setSequence - Setter for the game sequence array.
 * @param {Function} dependencies.setTotalTrials - Setter for the total trials count.
 * @param {Function} dependencies.setNBack - Setter for the N-back level state.
 * @param {Function} dependencies.setDifficulty - Setter for the difficulty state.
 * @param {Function} dependencies.setConsecutiveSuccesses - Setter for the consecutive successes state.
 * @param {Function} dependencies.setSaveToProfile - Setter for the saveToProfile flag state.
 * @param {Function} dependencies.updateProfileProgress - Action from ProfileContext to update profile data.
 * @param {Function} dependencies.completeChallengeLogic - Logic function to handle challenge completion.
 * @param {Function} dependencies.setPauseReason - Setter for the pause reason state.
 * @param {React.MutableRefObject<string>} dependencies.latestGameStateRef - Ref to the latest game state.
 * @param {React.MutableRefObject<number>} dependencies.nBackRef - Ref to the latest N-back level.
 * @param {React.MutableRefObject<number>} dependencies.difficultyRef - Ref to the latest difficulty.
 * @param {Function} dependencies.showLoading - Function to show loading indicator.
 * @param {Function} dependencies.hideLoading - Function to hide loading indicator.
 * @param {React.MutableRefObject<number>} dependencies.consecutiveSuccessesRef - Ref to the latest streak count.
 * @param {React.MutableRefObject<number>} dependencies.currentTrialRef - Ref to the latest trial index.
 * @param {React.MutableRefObject<number>} dependencies.totalTrialsRef - Ref to the latest total trials count.
 * @param {React.MutableRefObject<Function|null>} dependencies.onNavigateRef - Ref holding the navigate function.
 * @param {React.MutableRefObject<boolean>} dependencies.isEndingRef - Ref indicating if the ending process is active.
 * @param {React.MutableRefObject<number[]>} dependencies.visualResponsesRef - Ref to user's visual responses.
 * @param {React.MutableRefObject<number[]>} dependencies.audioResponsesRef - Ref to user's audio responses.
 * @param {React.MutableRefObject<object|null>} dependencies.sessionSettingsRef - Ref to current session's settings.
 * @param {React.MutableRefObject<number|null>} dependencies.sessionStartTimeRef - Ref to session start timestamp.
 * @param {React.MutableRefObject<object[]>} dependencies.replayEventsRef - Ref to replay event log.
 * @returns {Function} An async function that ends the game, optionally skipping results calculation.
 */
export function createEndGameAction({
    // Dependencies passed from GameContext
    activeProfile, isPlaygroundSession, saveToProfile, challengeState,
    setChallengeState, logReplayEvent, setGameState, setGameResults,
    setFeedbackMessage, setIsPlaygroundSession, setPlaygroundSettings,
    setCurrentTrial, setSequence, setTotalTrials, setNBack, setDifficulty,
    setConsecutiveSuccesses, setSaveToProfile, updateProfileProgress,
    completeChallengeLogic,
    setPauseReason, // Setter for pause reason
    latestGameStateRef, nBackRef, difficultyRef, 
    showLoading,
    hideLoading,
    consecutiveSuccessesRef, currentTrialRef, totalTrialsRef, onNavigateRef,
    isEndingRef, visualResponsesRef, audioResponsesRef, sessionSettingsRef,
    sessionStartTimeRef, replayEventsRef,
}) {

    /**
     * The core logic function returned by the factory. Ends the game session.
     * @param {object} [options={}] - Options to modify behavior.
     * @param {boolean} [options.skipResultsProcessing=false] - If true, skips backend call, results calculation, and showing results screen. Used for abandoning session.
     */
    return async (options = { skipResultsProcessing: false }) => {
        const functionStartTime = Date.now(); // Capture start time early for consistent logging ID
        // --- Guard Clauses & Initialization ---
        if (!isEndingRef) { // Check if the ref object itself is missing
            console.error(`[gameEnd Logic ${functionStartTime}] CRITICAL ABORT: isEndingRef object is missing.`);
            // Attempt to set a feedback message if possible, though other things might be broken too
            if (typeof setFeedbackMessage === 'function') {
                setFeedbackMessage("Critical error: Game ending mechanism is broken.");
            }
            if (typeof setGameState === 'function') {
                setGameState('idle'); // Try to reset to a safe state
            }
            return;
        }

        if (isEndingRef.current) { 
            console.warn(`[gameEnd Logic ${functionStartTime}] Aborted: Already ending.`); 
            return; 
        }
        
        const localGameState = latestGameStateRef?.current;
        if (localGameState === 'results' || localGameState === 'idle') { 
            console.warn(`[gameEnd Logic ${functionStartTime}] Aborted: Game already in state '${localGameState}'.`); 
            return; 
        }
        
        isEndingRef.current = true; // Set the flag immediately
        console.log(`[gameEnd Logic ${functionStartTime}] Initiated. State: ${localGameState}, Options:`, options);
        
        // Ensure a default value for endTime for logging in finally block, even if an early error occurs
        let endTime = Date.now(); 

        try {
            endTime = Date.now(); // Capture actual end time before processing
            if (typeof logReplayEvent === 'function') logReplayEvent({ type: 'gameEnd', timestamp: endTime });

            // --- Capture state/refs needed ---
            const currentTrial = currentTrialRef?.current;
            const totalTrialsPlanned = totalTrialsRef?.current;
            const navigate = onNavigateRef?.current;
            const startTime = sessionStartTimeRef?.current;
            const settingsFromSession = sessionSettingsRef?.current;
            const isCurrentPlayground = isPlaygroundSession;
            const currentNBackContext = nBackRef?.current; 
            const currentDifficultyContext = difficultyRef?.current; 
            const currentStreakContext = consecutiveSuccessesRef?.current;
            const currentHighestNBackGoodContext = typeof activeProfile?.highestNBackGood === 'number' ? activeProfile.highestNBackGood : 0;

            // --- Minimal Reset and Exit Logic (if skipping results) ---
            if (options.skipResultsProcessing) {
                console.log(`[gameEnd Logic ${functionStartTime}] Skipping results processing due to options.`);
                if (typeof logReplayEvent === 'function') {
                    logReplayEvent({ type: 'sessionDiscarded', reason: 'user_confirmed_leave', trialsCompleted: (currentTrial ?? -1) + 1 });
                }
                // Minimal state reset
                setGameState('idle'); setCurrentTrial(-1); setGameResults(null); setFeedbackMessage('');
                setSequence([]); setTotalTrials(0); setPauseReason(null);
                if (visualResponsesRef) visualResponsesRef.current = []; if (audioResponsesRef) audioResponsesRef.current = [];
                if (sessionSettingsRef) sessionSettingsRef.current = null; if (replayEventsRef) replayEventsRef.current = [];
                if (sessionStartTimeRef) sessionStartTimeRef.current = null;
                if (isCurrentPlayground) { setIsPlaygroundSession(false); setPlaygroundSettings(null); }
                if (challengeState?.isActive) { setChallengeState({ isActive: false, targetNBack: null, wins: 0 }); }
                setSaveToProfile(true);
                console.log(`[gameEnd Logic ${functionStartTime}] Finished minimal reset for skipResults.`);
                return; // Exit (finally block will still run to reset isEndingRef)
            }

            // --- Standard Game End Flow ---
            if (currentTrial === undefined || totalTrialsPlanned === undefined || startTime === null || !settingsFromSession ||
                currentNBackContext === undefined || currentDifficultyContext === undefined || currentStreakContext === undefined) {
                console.error(`[gameEnd Logic ${functionStartTime}] Error: Missing critical session refs/state for standard end. Aborting.`);
                setGameState('idle'); setFeedbackMessage("Error ending game: Critical data missing.");
                setPauseReason(null);
                return; // Exit (finally block will still run)
            }

            // --- Handle standard session discard (too few trials) ---
            const isChallengeAttempt = settingsFromSession?.isChallengeAttempt || false;
            const trialsCompleted = Math.max(0, (currentTrial ?? -1) + 1);
            const endedEarly = trialsCompleted < (totalTrialsPlanned ?? 0);
            const discardThreshold = Math.max(1, Math.min(10, totalTrialsPlanned > 0 ? Math.ceil(totalTrialsPlanned * 0.5) : 10));
            if (!isChallengeAttempt && endedEarly && trialsCompleted < discardThreshold) {
                 console.warn(`[gameEnd Logic ${functionStartTime}] Session discarded locally (completed ${trialsCompleted} < threshold ${discardThreshold}). Resetting state and navigating to dashboard.`);
                 if (typeof logReplayEvent === 'function') {
                    logReplayEvent({ type: 'sessionDiscarded', reason: 'early_quit_below_threshold', trialsCompleted, discardThreshold });
                 }
                 // Full state reset and navigate to dashboard
                 setGameState('idle'); setCurrentTrial(-1); setGameResults(null); setFeedbackMessage(''); setSequence([]); setTotalTrials(0);
                 setPauseReason(null);
                 if (visualResponsesRef) visualResponsesRef.current = []; if (audioResponsesRef) audioResponsesRef.current = [];
                 if (sessionSettingsRef) sessionSettingsRef.current = null; if (replayEventsRef) replayEventsRef.current = [];
                 if (sessionStartTimeRef) sessionStartTimeRef.current = null; if (isCurrentPlayground) { setIsPlaygroundSession(false); setPlaygroundSettings(null); }
                 if (challengeState?.isActive) { setChallengeState({ isActive: false, targetNBack: null, wins: 0 }); }
                 setSaveToProfile(true);
                 if (activeProfile) { setNBack(activeProfile.currentNBack ?? 2); setDifficulty(Math.max(0, Math.min(0.99, activeProfile.currentDifficulty ?? 0.0))); setConsecutiveSuccesses(activeProfile.consecutiveSuccesses ?? 0); }
                 else { setNBack(2); setDifficulty(0.0); setConsecutiveSuccesses(0); }
                 if (navigate && typeof navigate === 'function') { queueMicrotask(() => navigate('/dashboard', { replace: true })); }
                 else { console.error("[gameEnd Logic] Navigate handler missing after discard."); }
                 return; // Exit (finally block will still run)
            }

            // --- Prepare payload for backend results calculation ---
            if (!visualResponsesRef || !audioResponsesRef || !replayEventsRef) { console.error(`[gameEnd Logic ${functionStartTime}] Missing response/replay refs!`); setFeedbackMessage("Error: Cannot save results, missing data."); setGameState('idle'); /* finally handles isEndingRef */ return; }
            const visualResponsesPlayed = visualResponsesRef.current.filter(idx => idx < trialsCompleted);
            const audioResponsesPlayed = audioResponsesRef.current.filter(idx => idx < trialsCompleted);
            let sanitizedEvents = []; let serializableSettingsForReplay = {};
            try {
                const settingsToSanitize = {
                     nBack: settingsFromSession?.nBack, difficulty: settingsFromSession?.difficulty,
                     calculatedISI: settingsFromSession?.calculatedISI, stimulusDuration: settingsFromSession?.stimulusDuration,
                     totalTrials: settingsFromSession?.totalTrials, isPlayground: settingsFromSession?.isPlayground,
                     isChallengeAttempt: settingsFromSession?.isChallengeAttempt,
                     // Include new variability params if they exist in settingsFromSession
                     baseVariabilityOffsetMs: settingsFromSession?.baseVariabilityOffsetMs,
                     maxRandomVariabilityOffsetMs: settingsFromSession?.maxRandomVariabilityOffsetMs,
                };
                serializableSettingsForReplay = sanitizeForJSON(settingsToSanitize);
                if (Array.isArray(replayEventsRef.current)) { sanitizedEvents = replayEventsRef.current.map(event => sanitizeForJSON(event)); }
            } catch (sanitizeError) { console.error(`[gameEnd Logic ${functionStartTime}] Error sanitizing replay data:`, sanitizeError); setFeedbackMessage("Warning: Could not fully prepare replay data."); }
            const replayData = { version: 1, startTime: startTime, settings: serializableSettingsForReplay, events: sanitizedEvents };
            const shouldSaveProgress = !settingsFromSession?.isPlayground && !settingsFromSession?.isChallengeAttempt && saveToProfile;

            const preSessionContext = {
                originalNBack: currentNBackContext,
                originalDifficulty: currentDifficultyContext,
                originalConsecutiveSuccesses: currentStreakContext,
                originalHighestAchievedNBack: activeProfile?.highestNBack ?? currentNBackContext,
                originalHighestNBackGood: currentHighestNBackGoodContext
            };

            if (typeof preSessionContext.originalConsecutiveSuccesses !== 'number' || isNaN(preSessionContext.originalConsecutiveSuccesses)) { console.error("preSessionContext invalid", preSessionContext); setFeedbackMessage("Error: Cannot save results, invalid internal state."); setGameState('idle'); /* finally handles isEndingRef */ return; }
            const sequencePlayed = settingsFromSession?.generatedSequence?.slice(0, trialsCompleted) || [];
            
            // Construct sessionDataForPayload, ensuring all expected fields (including new variability ones) are present
            const sessionDataForPayload = { 
                nBack: Number(settingsFromSession?.nBack ?? 0), 
                difficulty: Number(settingsFromSession?.difficulty ?? 0), 
                calculatedISI: Number(settingsFromSession?.calculatedISI ?? 0), 
                stimulusDuration: Number(settingsFromSession?.stimulusDuration ?? 0),
                baseVariabilityOffsetMs: Number(settingsFromSession?.baseVariabilityOffsetMs ?? 0), // Ensure these are numbers, default 0
                maxRandomVariabilityOffsetMs: Number(settingsFromSession?.maxRandomVariabilityOffsetMs ?? 0), // Ensure these are numbers, default 0
                totalTrialsPlanned: Number(totalTrialsPlanned ?? 0), 
                trialsCompleted: Number(trialsCompleted ?? 0), 
                isPlayground: Boolean(isCurrentPlayground ?? false), 
                isChallengeAttempt: Boolean(isChallengeAttempt ?? false), 
                isPartialResult: Boolean(endedEarly ?? false), 
                showReport: settingsFromSession?.showReport !== false, 
                saveToProfile: Boolean(shouldSaveProgress ?? false), 
                totalTrials: Number(settingsFromSession?.totalTrials ?? totalTrialsPlanned ?? 0), 
                sequence: sequencePlayed, 
                preSessionProfileContext: preSessionContext 
            };


            let validationOk = true;
            if (typeof sessionDataForPayload.nBack !== 'number' || sessionDataForPayload.nBack < 1) { validationOk = false; console.error("Validation fail: nBack", sessionDataForPayload.nBack); }
            if (typeof sessionDataForPayload.difficulty !== 'number' || 
                (sessionDataForPayload.difficulty < 0 && !sessionDataForPayload.isPlayground) || 
                sessionDataForPayload.difficulty > 1) { 
                validationOk = false; console.error("Validation fail: difficulty", sessionDataForPayload.difficulty); 
            }
            if (typeof sessionDataForPayload.trialsCompleted !== 'number' || sessionDataForPayload.trialsCompleted < 0) { validationOk = false; console.error("Validation fail: trialsCompleted", sessionDataForPayload.trialsCompleted); }
            // Also validate new variability params if present (they should be numbers)
            if (sessionDataForPayload.baseVariabilityOffsetMs === undefined || typeof sessionDataForPayload.baseVariabilityOffsetMs !== 'number') { validationOk = false; console.error("Validation fail: baseVariabilityOffsetMs", sessionDataForPayload.baseVariabilityOffsetMs); }
            if (sessionDataForPayload.maxRandomVariabilityOffsetMs === undefined || typeof sessionDataForPayload.maxRandomVariabilityOffsetMs !== 'number') { validationOk = false; console.error("Validation fail: maxRandomVariabilityOffsetMs", sessionDataForPayload.maxRandomVariabilityOffsetMs); }


            if (!validationOk) { console.error(`[gameEnd Logic ${functionStartTime}] Frontend payload validation failed! Aborting backend call.`, sessionDataForPayload); setFeedbackMessage("Error: Failed internal validation before saving."); setGameState('idle'); /* finally handles isEndingRef */ return; }
            console.log(`[gameEnd Logic ${functionStartTime}] Constructed and validated sessionDataForPayload.`);
            const payload = { profileId: activeProfile?.id || null, sessionData: sessionDataForPayload, userVisualResponses: visualResponsesPlayed, userAudioResponses: audioResponsesPlayed, replayData: replayData, timing: { startTime: startTime, endTime: endTime }};

            // --- Call Backend ---
            let fetchedBackendResults = null; let backendError = null;
            const backendCallStart = Date.now();
            console.log(`[gameEnd Logic ${functionStartTime}] Preparing to call backend for results...`);
            
            // Show loading indicator before the backend call
            if (typeof showLoading === 'function') {
                showLoading("Finalizing session...");
            }

            try {
                 if (!auth.currentUser) throw new Error("User not authenticated.");
                 const idToken = await auth.currentUser.getIdToken(true);
                 let requestBody = JSON.stringify(payload);
                 const response = await fetch(calculateResultsUrl, { method: 'POST', headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' }, body: requestBody });
                 const responseText = await response.text();
                 if (!response.ok) { let msg = `Backend Error (${response.status})`; try{ const errJson = JSON.parse(responseText); msg = errJson.message || errJson.error || msg; } catch(e){} throw new Error(msg); }
                 try { fetchedBackendResults = JSON.parse(responseText); } catch(e){ console.error("Backend response JSON parse error:", e, "Response text:", responseText); throw new Error("Received invalid JSON from backend.")}
                 console.log(`[gameEnd Logic ${functionStartTime}] Backend results parsed successfully.`);

                if (shouldSaveProgress && activeProfile?.id && typeof updateProfileProgress === 'function') {
                     console.log(`[gameEnd Logic ${functionStartTime}] Updating local profile progress...`);
                    if (typeof fetchedBackendResults?.newNBack === 'number' &&
                        typeof fetchedBackendResults?.newDifficulty === 'number' &&
                        typeof fetchedBackendResults?.newConsecutiveSuccesses === 'number') {

                        let newPreviousDifficulties = activeProfile.previousNBackLevelDifficulties || {};
                        if (fetchedBackendResults.newNBack !== currentNBackContext) {
                            newPreviousDifficulties = {
                                ...newPreviousDifficulties,
                                [currentNBackContext]: currentDifficultyContext 
                            };
                             console.log(`[gameEnd Logic ${functionStartTime}] N-level changed by d'algo (from ${currentNBackContext} to ${fetchedBackendResults.newNBack}). Storing difficulty ${currentDifficultyContext} for N-level ${currentNBackContext}.`);
                        }
                        const updatePayload = {
                            currentNBack: fetchedBackendResults.newNBack,
                            currentDifficulty: fetchedBackendResults.newDifficulty,
                            consecutiveSuccesses: fetchedBackendResults.newConsecutiveSuccesses,
                            ...(fetchedBackendResults.newNBack > (activeProfile.highestNBack || 0) && { highestNBack: fetchedBackendResults.newNBack }),
                            ...(fetchedBackendResults.isNewHighestNBackGood && {
                                highestNBackGood: fetchedBackendResults.sessionNBack, 
                                highestAchievedGoodSessionId: fetchedBackendResults.sessionId 
                            }),
                            previousNBackLevelDifficulties: newPreviousDifficulties,
                        };
                        console.log(`[gameEnd Logic ${functionStartTime}] Constructed updatePayload for profile:`, updatePayload);
                        await updateProfileProgress(activeProfile.id, updatePayload);
                        console.log(`[gameEnd Logic ${functionStartTime}] LOCAL updateProfileProgress finished.`);
                    } else { console.error('[gameEnd Logic] Backend results missing fields for profile update.', fetchedBackendResults); }
                } else {
                    if (!shouldSaveProgress) console.log(`[gameEnd Logic ${functionStartTime}] Skipping profile update: shouldSaveProgress is false.`);
                    else console.warn(`[gameEnd Logic ${functionStartTime}] Cannot update profile: Missing activeProfile.id or updateProfileProgress function.`);
                }
            } catch (error) {
                 console.error(`[gameEnd Logic ${functionStartTime}] Backend call/processing failed:`, error);
                 backendError = error;
                 setFeedbackMessage(error.message.includes("Backend Error") ? error.message : `Error processing results: ${error.message}. Please try again.`);
                 fetchedBackendResults = null; // Ensure it's null on error
            } finally {
                // Hide loading indicator after the backend call attempt (success or failure)
                if (typeof hideLoading === 'function') {
                    hideLoading();
                }
            }
            console.log(`[gameEnd Logic ${functionStartTime}] Backend call duration: ${Date.now() - backendCallStart}ms`);

            // --- State Transition Logic ---
            console.log(`[gameEnd Logic ${functionStartTime}] --- Entering State Transition Logic ---`);
            // try...finally for state transition is inside the main try/finally of the function
            if (fetchedBackendResults && fetchedBackendResults.isChallengeAttempt === true) {
                 const challengeSuccessful = (fetchedBackendResults.overallDprime ?? -Infinity) >= (fetchedBackendResults.goodThreshold ?? Infinity);
                 if (challengeSuccessful) {
                     console.log(`[gameEnd Logic ${functionStartTime}] Challenge SUCCESSFUL.`);
                     if (typeof completeChallengeLogic === 'function') await completeChallengeLogic();
                     else { console.error(`[gameEnd Logic ${functionStartTime}] completeChallengeLogic missing!`); setGameState('idle'); }
                 } else {
                     console.log(`[gameEnd Logic ${functionStartTime}] Challenge FAILED.`);
                     setFeedbackMessage('');
                     setGameResults(fetchedBackendResults);
                     setGameState('results');
                     setChallengeState({ isActive: false, targetNBack: null, wins: 0 });
                 }
            } else if (fetchedBackendResults && fetchedBackendResults.showReport !== false) {
                 console.log(`[gameEnd Logic ${functionStartTime}] Standard/Playground with report. Setting results state.`);
                 const feedback = generateFeedbackMessage(fetchedBackendResults, preSessionContext?.originalDifficulty);
                 setFeedbackMessage(feedback || (backendError ? `Error: ${backendError.message}` : 'Session complete.'));
                 setGameResults(fetchedBackendResults);
                 setGameState('results');
                 if (challengeState.isActive) { setChallengeState({ isActive: false, targetNBack: null, wins: 0 }); }
            } else {
                 console.log(`[gameEnd Logic ${functionStartTime}] No report or backend fail/results missing. Setting idle state.`);
                 setFeedbackMessage(backendError ? backendError.message : 'Session ended without results.');
                 setGameResults(null);
                 setGameState('idle');
                 setCurrentTrial(-1); setSequence([]); setTotalTrials(0);
                 if (visualResponsesRef) visualResponsesRef.current = []; if (audioResponsesRef) audioResponsesRef.current = [];
                 if (sessionSettingsRef) sessionSettingsRef.current = null; if (replayEventsRef) replayEventsRef.current = [];
                 if (sessionStartTimeRef) sessionStartTimeRef.current = null; if (isCurrentPlayground) { setIsPlaygroundSession(false); setPlaygroundSettings(null); }
                 if (challengeState?.isActive) { setChallengeState({ isActive: false, targetNBack: null, wins: 0 }); }
                 setSaveToProfile(true);
                 if (activeProfile && fetchedBackendResults) { 
                     setNBack(fetchedBackendResults.newNBack ?? currentNBackContext ?? 2);
                     setDifficulty(fetchedBackendResults.newDifficulty ?? currentDifficultyContext ?? 0.0);
                     setConsecutiveSuccesses(fetchedBackendResults.newConsecutiveSuccesses ?? currentStreakContext ?? 0);
                 } else if (activeProfile) { 
                     setNBack(activeProfile.currentNBack ?? 2);
                     setDifficulty(Math.max(0, Math.min(0.99, activeProfile.currentDifficulty ?? 0.0)));
                     setConsecutiveSuccesses(activeProfile.consecutiveSuccesses ?? 0);
                 } else { 
                     setNBack(2); setDifficulty(0.0); setConsecutiveSuccesses(0);
                 }
                 if (navigate && typeof navigate === 'function') { queueMicrotask(() => navigate('/dashboard', { replace: true })); }
                 else { console.error("[gameEnd Logic] Navigate handler missing after backend fail/no report."); }
            }
            setPauseReason(null);
            console.log(`[gameEnd Logic ${functionStartTime}] --- Finished State Transition Logic --- New State: ${latestGameStateRef?.current}`);
            // } // Inner try removed
        } finally {
            // Ensure isEndingRef is reset regardless of how the function exits.
            // The setTimeout provides a brief window to allow other state updates to settle
            // and to prevent overly rapid re-entry if a click event is somehow doubled.
            setTimeout(() => {
                if (isEndingRef) { 
                    isEndingRef.current = false; 
                    console.log(`[gameEnd Logic ${functionStartTime}] Reset isEndingRef (finally block). Total endGameLogic time: ${Date.now() - functionStartTime}ms`); 
                } else { 
                    // This case should ideally not be hit if the initial guard is correct
                    console.warn("[gameEnd Logic] isEndingRef missing in finally during reset attempt!"); 
                }
            }, 50); // Reduced delay for faster reset, adjust if needed
        }
    };
}